import React, { useCallback, useEffect, useState } from 'react';
import {
  Button, Card, Checkbox, DatePicker, Empty, Form, Input, InputNumber, Modal,
  Popconfirm, Select, Space, Table, Tag, TimePicker, Typography, message,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  createAdminTask, createWorkSession, deleteAdminTask, deleteWorkSession,
  getAdminTasks, getStageTeams, getWorkSessions, updateAdminTask, updateWorkSession,
} from '../../api/competitions';
import apiErrorMessage from '../../utils/apiError';

const { Text } = Typography;

const StageOperationsTab = ({ stage }) => (
  <Space orientation="vertical" size={16} style={{ width: '100%' }}>
    <WorkSessions stage={stage} />
    <AdminTasks stage={stage} />
  </Space>
);

// ------------------------------------------------------- Takım çalışmaları

const WorkSessions = ({ stage }) => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ session_count: 0, total_minutes: 0 });
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ open: false, record: null });
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, { data: st }] = await Promise.all([
        getWorkSessions(stage.id),
        getStageTeams(stage.id),
      ]);
      setRows(data.data || []);
      setSummary(data.summary || {});
      setTeams((st.data || []).map((x) => x.team).filter(Boolean));
    } catch (e) {
      message.error(apiErrorMessage(e, 'Çalışmalar yüklenemedi.'));
    } finally {
      setLoading(false);
    }
  }, [stage.id]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      const v = await form.validateFields();
      const payload = {
        ...v,
        date: v.date.format('YYYY-MM-DD'),
        start_time: v.start_time ? v.start_time.format('HH:mm') : null,
      };
      if (modal.record) await updateWorkSession(modal.record.id, payload);
      else await createWorkSession(stage.id, payload);
      message.success('Kaydedildi.');
      setModal({ open: false, record: null });
      form.resetFields();
      load();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(apiErrorMessage(e));
    }
  };

  return (
    <Card
      size="small"
      title="Takım Çalışmaları"
      extra={
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          onClick={() => { setModal({ open: true, record: null }); form.setFieldsValue({ date: dayjs() }); }}
        >
          Çalışma Ekle
        </Button>
      }
    >
      <Text type="secondary">
        Normal kurs derslerinden tamamen ayrıdır — ders programına, yoklamaya ve
        dersliklere hiç dokunmaz. Dışarıdan gelen eğitmeni isim olarak yazabilirsiniz.
      </Text>
      <Space style={{ margin: '8px 0' }}>
        <Tag>{summary.session_count || 0} çalışma</Tag>
        <Tag>{Math.round((summary.total_minutes || 0) / 60 * 10) / 10} saat</Tag>
      </Space>

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={rows}
        pagination={false}
        locale={{ emptyText: <Empty description="Henüz çalışma kaydı yok." /> }}
        columns={[
          { title: 'Tarih', dataIndex: 'date', width: 120, render: (v) => dayjs(v).format('DD.MM.YYYY') },
          { title: 'Saat', dataIndex: 'start_time', width: 80, render: (v) => (v ? String(v).slice(0, 5) : '—') },
          {
            title: 'Süre',
            dataIndex: 'duration_minutes',
            width: 90,
            render: (v) => (v ? `${v} dk` : '—'),
          },
          { title: 'Takım', dataIndex: ['team', 'name'], render: (v) => v || <Text type="secondary">Takımsız</Text> },
          {
            title: 'Eğitmen',
            render: (_, r) =>
              r.instructor
                ? `${r.instructor.name} ${r.instructor.surname}`
                : (r.instructor_name || '—'),
          },
          { title: 'Not', dataIndex: 'notes', render: (v) => v || '—' },
          {
            title: '',
            width: 100,
            fixed: 'right',
            render: (_, r) => (
              <Space>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => {
                    setModal({ open: true, record: r });
                    form.setFieldsValue({
                      ...r,
                      date: dayjs(r.date),
                      start_time: r.start_time ? dayjs(String(r.start_time).slice(0, 5), 'HH:mm') : null,
                    });
                  }}
                />
                <Popconfirm
                  title="Kayıt silinsin mi?"
                  onConfirm={async () => {
                    try {
                      await deleteWorkSession(r.id);
                      message.success('Silindi.');
                      load();
                    } catch (e) { message.error(apiErrorMessage(e)); }
                  }}
                  okText="Sil"
                  cancelText="Vazgeç"
                >
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />

      <Modal
        title={modal.record ? 'Çalışmayı Düzenle' : 'Çalışma Ekle'}
        open={modal.open}
        onOk={submit}
        onCancel={() => { setModal({ open: false, record: null }); form.resetFields(); }}
        okText="Kaydet"
        cancelText="Vazgeç"
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item name="competition_team_id" label="Takım">
            <Select allowClear placeholder="Takım seçin" options={teams.map((t) => ({ value: t.id, label: t.name }))} />
          </Form.Item>
          <Space>
            <Form.Item name="date" label="Tarih" rules={[{ required: true, message: 'Tarih seçin.' }]}>
              <DatePicker format="DD.MM.YYYY" />
            </Form.Item>
            <Form.Item name="start_time" label="Saat"><TimePicker format="HH:mm" /></Form.Item>
            <Form.Item name="duration_minutes" label="Süre (dk)"><InputNumber min={1} max={1440} /></Form.Item>
          </Space>
          <Form.Item
            name="instructor_name"
            label="Eğitmen"
            extra="Kurum dışından gelen eğitmenin adını buraya yazın."
          >
            <Input />
          </Form.Item>
          <Form.Item name="notes" label="Not">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

// ---------------------------------------------------- Yönetici görev takibi

const AdminTasks = ({ stage }) => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ open: 0, overdue: 0 });
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ open: false, record: null });
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getAdminTasks(stage.id);
      setRows(data.data || []);
      setSummary(data.summary || {});
    } catch (e) {
      message.error(apiErrorMessage(e, 'Görevler yüklenemedi.'));
    } finally {
      setLoading(false);
    }
  }, [stage.id]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      const v = await form.validateFields();
      const payload = { ...v, due_date: v.due_date ? v.due_date.format('YYYY-MM-DD') : null };
      if (modal.record) await updateAdminTask(modal.record.id, payload);
      else await createAdminTask(stage.id, payload);
      message.success('Kaydedildi.');
      setModal({ open: false, record: null });
      form.resetFields();
      load();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(apiErrorMessage(e));
    }
  };

  return (
    <Card
      size="small"
      title="Yönetici Görevleri (başvuru takibi)"
      extra={
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          onClick={() => { setModal({ open: true, record: null }); form.resetFields(); }}
        >
          Görev Ekle
        </Button>
      }
    >
      <Text type="secondary">Bu bölüm veliye hiçbir şekilde görünmez.</Text>
      <Space style={{ margin: '8px 0' }}>
        <Tag>{summary.open || 0} açık</Tag>
        {summary.overdue > 0 && <Tag color="error">{summary.overdue} gecikmiş</Tag>}
      </Space>

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={rows}
        pagination={false}
        locale={{ emptyText: <Empty description="Görev eklenmemiş." /> }}
        columns={[
          {
            title: '',
            width: 40,
            render: (_, r) => (
              <Checkbox
                checked={r.is_done}
                onChange={async (e) => {
                  try {
                    await updateAdminTask(r.id, { is_done: e.target.checked });
                    load();
                  } catch (err) { message.error(apiErrorMessage(err)); }
                }}
              />
            ),
          },
          {
            title: 'Görev',
            dataIndex: 'title',
            render: (v, r) => (r.is_done ? <Text delete type="secondary">{v}</Text> : v),
          },
          {
            title: 'Son Tarih',
            dataIndex: 'due_date',
            width: 130,
            render: (v, r) => {
              if (!v) return '—';
              const overdue = !r.is_done && dayjs(v).isBefore(dayjs(), 'day');
              return <Text type={overdue ? 'danger' : undefined}>{dayjs(v).format('DD.MM.YYYY')}</Text>;
            },
          },
          { title: 'Not', dataIndex: 'notes', render: (v) => v || '—' },
          {
            title: '',
            width: 100,
            fixed: 'right',
            render: (_, r) => (
              <Space>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => {
                    setModal({ open: true, record: r });
                    form.setFieldsValue({ ...r, due_date: r.due_date ? dayjs(r.due_date) : null });
                  }}
                />
                <Popconfirm
                  title="Görev silinsin mi?"
                  onConfirm={async () => {
                    try {
                      await deleteAdminTask(r.id);
                      message.success('Silindi.');
                      load();
                    } catch (e) { message.error(apiErrorMessage(e)); }
                  }}
                  okText="Sil"
                  cancelText="Vazgeç"
                >
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />

      <Modal
        title={modal.record ? 'Görevi Düzenle' : 'Yeni Görev'}
        open={modal.open}
        onOk={submit}
        onCancel={() => { setModal({ open: false, record: null }); form.resetFields(); }}
        okText="Kaydet"
        cancelText="Vazgeç"
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="Görev" rules={[{ required: true, message: 'Görev girin.' }]}>
            <Input placeholder="Fibonacci başvuru formunu gönder" />
          </Form.Item>
          <Form.Item name="due_date" label="Son Tarih">
            <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="notes" label="Not">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default StageOperationsTab;
